import React, { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import './QuestionAppearedConditionModal.css';
import {
  Condition,
  CONNECTOR_OPTIONS,
  FormItem,
  FormQuestionItem,
  isPageBreak,
  OPERATOR_OPTIONS,
  Question,
} from '../../types';
import Select from 'react-select';
import { Button } from 'antd';
import { getPlainText } from '../../utils/InputType';

type QuestionAppearedConditionModalProps = {
  questions: FormItem[];
  question: Question;
  open: boolean;
  onClose: () => void;
  onSave: (conditions: Condition[]) => void;
};

const QuestionAppearedConditionModal: React.FC<QuestionAppearedConditionModalProps> = ({
  questions,
  question,
  open,
  onClose,
  onSave,
}) => {
  const [localConditions, setLocalConditions] = useState<Condition[]>([]);

  useEffect(() => {
    if (open) {
      const clonedConditions = (question.appeared_conditions || []).map((condition) => ({
        ...condition,
        values: [...condition.values],
      }));
      setLocalConditions(clonedConditions);
    }
  }, [open, question.appeared_conditions]);

  const addCondition = () => {
    const newCondition: Condition = {
      connected_question_guid: '',
      operator: '',
      values: [''],
      connector:
        localConditions.length === 0 || !localConditions[0]?.connector
          ? CONNECTOR_OPTIONS[0].value
          : localConditions[0].connector,
    };
    setLocalConditions([...localConditions, newCondition]);
  };

  const removeCondition = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    if (newConditions.length > 0 && newConditions[0].connector !== CONNECTOR_OPTIONS[0].value) {
      newConditions[0].connector = CONNECTOR_OPTIONS[0].value; // Reset first condition to 'first'
    }
    setLocalConditions(newConditions);
  };

  const updateCondition = (index: number, field: string, value: string) => {
    const newConditions = [...localConditions];
    if (field === 'operator') {
      newConditions[index].values = [''];
    }

    newConditions[index] = {
      ...newConditions[index],
      [field]: value,
    };

    setLocalConditions(newConditions);
  };

  const updateConditionConnector = (idx: number, value: string) => {
    const updated = [...localConditions];
    updated[idx].connector = value;
    for (let i = idx + 1; i < updated.length; i++) {
      updated[i].connector = value;
    }
    setLocalConditions(updated);
  };

  const updateConditionValue = (conditionIndex: number, values: string[]) => {
    const newConditions = [...localConditions];
    newConditions[conditionIndex].values = values;
    setLocalConditions(newConditions);
  };

  const getSelectedQuestion = (questionGuid: string): FormQuestionItem | undefined => {
    return questions
      .filter((q): q is FormQuestionItem => !isPageBreak(q))
      .find((q) => q.guid === questionGuid);
  };

  const getQuestionOptions = (questionGuid: string) => {
    const selectedQuestion = getSelectedQuestion(questionGuid);
    if (!selectedQuestion || !selectedQuestion.options) return [];
    return selectedQuestion.options;
  };

  const handleSave = () => {
    onSave(localConditions);
    onClose();
  };

  const handleCancel = () => {
    setLocalConditions([...(question.appeared_conditions || [])]);
    onClose();
  };

  const isDropdownOperator = (operator: string) => {
    return operator === 'contains' || operator === 'not_contains';
  };

  const areAllConditionsValid = () => {
    return (
      localConditions.length > 0 &&
      localConditions.every(
        (condition) =>
          condition.connected_question_guid?.trim() &&
          condition.operator?.trim() &&
          Array.isArray(condition.values) &&
          condition.values.length > 0 &&
          condition.values.every((val) => val?.trim() !== ''),
      )
    );
  };

  if (!open) return null;

  return (
    <div className='modal-overlay'>
      <div className='modal-container'>
        <div className='modal-header'>
          <h2 className='modal-title'>ניהול תנאים</h2>
          <button onClick={handleCancel} className='modal-close-btn'>
            <X size={24} />
          </button>
        </div>

        <div className='modal-body'>
          {localConditions.length === 0 ? (
            <div className='empty-state'>
              אין תנאים מוגדרים. לחצו על "הוסף תנאי" כדי להוסיף אחד.
            </div>
          ) : (
            <div className='conditions-list'>
              {localConditions.map((condition, index) => (
                <div key={index} className='condition-row'>
                  <div className='condition-fields'>
                    <div className='field-group'>
                      <Select
                        classNamePrefix='connector-select'
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        value={
                          index === 0
                            ? CONNECTOR_OPTIONS[0]
                            : CONNECTOR_OPTIONS.find(
                                (opt) =>
                                  opt.value ===
                                  (index === 1
                                    ? condition.connector
                                    : localConditions[1].connector),
                              ) || null
                        }
                        onChange={(option) => {
                          if (index === 1) {
                            updateConditionConnector(index, option?.value || '');
                          }
                        }}
                        options={CONNECTOR_OPTIONS.slice(1)}
                        placeholder='בחר מקשר'
                        isDisabled={index === 0 || index > 1}
                      />
                    </div>
                    <div className='field-group'>
                      <label className='condition-field-label field-label'>שאלה</label>
                      <Select
                        classNamePrefix='field-select'
                        value={
                          questions
                            .filter(
                              (q): q is FormQuestionItem =>
                                !isPageBreak(q) && q.guid === condition.connected_question_guid,
                            )
                            .map((q) => ({
                              value: q.guid,
                              label: getPlainText(q.text),
                            }))[0] || null
                        }
                        onChange={(option) =>
                          updateCondition(index, 'connected_question_guid', option?.value || '')
                        }
                        options={questions
                          .filter((q): q is FormQuestionItem => !isPageBreak(q))
                          .map((q) => ({
                            value: q.guid,
                            label: getPlainText(q.text),
                          }))}
                        placeholder='בחר שאלה'
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className='field-group'>
                      <label className='condition-field-label field-label'>אופרטור</label>
                      <Select
                        classNamePrefix='field-select'
                        value={
                          OPERATOR_OPTIONS.find((opt) => opt.value === condition.operator) || null
                        }
                        onChange={(option) =>
                          updateCondition(index, 'operator', option?.value || '')
                        }
                        options={OPERATOR_OPTIONS}
                        placeholder='בחר אופרטור'
                        isDisabled={!condition.connected_question_guid}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className='field-group'>
                      <label className='condition-field-label field-label'>ערך</label>
                      {isDropdownOperator(condition.operator) ? (
                        <Select
                          isMulti
                          closeMenuOnSelect={false}
                          value={getQuestionOptions(condition.connected_question_guid)
                            .filter((option) => condition.values.includes(option.guid))
                            .map((option) => ({
                              value: option.guid,
                              label: option.text,
                            }))}
                          onChange={(selectedOptions) => {
                            const values = selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [];
                            updateConditionValue(index, values);
                          }}
                          options={getQuestionOptions(condition.connected_question_guid).map(
                            (option) => ({
                              value: option.guid,
                              label: option.text,
                            }),
                          )}
                          placeholder='בחר'
                          isDisabled={!condition.connected_question_guid}
                          className='react-select-container'
                          classNamePrefix='react-select'
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      ) : (
                        <input
                          type='number'
                          value={condition.values[0] || ''}
                          onChange={(e) => updateConditionValue(index, [e.target.value])}
                          className='field-input'
                          placeholder='הכנס ערך'
                          disabled={!condition.connected_question_guid}
                        />
                      )}
                    </div>
                    <button onClick={() => removeCondition(index)} className='remove-btn'>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='modal-footer'>
          <Button onClick={addCondition} className='add-btn'>
            <Plus size={16} />
            הוסף תנאי
          </Button>
          <div className='footer-actions'>
            <Button onClick={handleSave} disabled={!areAllConditionsValid()} className='save-btn'>
              אישור
            </Button>
            <Button onClick={handleCancel} className='cancel-btn'>
              ביטול
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionAppearedConditionModal;
